import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { songsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { canEdit } from '../utils/permissions';
import Navbar from '../components/Navbar';
import { Trash2, Search, Music as MusicIcon, RefreshCw, X, ChevronLeft, ChevronRight, Calendar, Clock, CheckCircle, AlertCircle, ChevronDown, Music2, AlignLeft, Pencil, Maximize2, Plus, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { ChordProParser, HtmlDivFormatter } from 'chordsheetjs';
import { postProcessChordHtml, transposeKey, detectAccidental } from '../utils/chordpro';
import { getStoredCapo, setStoredCapo, parseCapoDirective, CAPO_MIN, CAPO_MAX } from '../utils/capo';
import SongViewerModal from '../components/SongViewerModal';
import DeleteButton from '../components/ui/DeleteButton';

/** Scoped CSS for ChordSheetJS HtmlDivFormatter output inside the drawer. */
const CHORD_SHEET_CSS = `
  .csj-wrap .paragraph { margin-bottom: 1.25rem; }
  .csj-wrap .row { display: flex; flex-wrap: wrap; line-height: 1; }
  .csj-wrap .column { display: inline-flex; flex-direction: column; }
  .csj-wrap .chord {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.75rem; font-weight: 700; color: #16a34a;
    line-height: 1.3; min-height: 1.3em; white-space: pre; padding-right: 0.4em;
  }
  .csj-wrap .chord:empty { visibility: hidden; }
  .csj-wrap .row.row--lyric-only .chord { display: none; min-height: 0; }
  .csj-wrap .row.row--chord-only .lyrics { display: none; min-height: 0; line-height: 0; }
  .csj-wrap .lyrics {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.875rem; line-height: 1.45; color: #1e293b;
    white-space: pre-wrap; word-break: break-word; overflow-wrap: break-word; padding-right: 0.4em;
  }
  .csj-wrap .comment {
    display: inline-block; font-size: 0.65rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.1em; color: #16a34a;
    background: rgba(22,163,74,0.1); border-radius: 0.25rem;
    padding: 0.1em 0.5em; margin-top: 0.25rem; margin-bottom: 0.125rem;
  }
  .dark .csj-wrap .chord   { color: #4ade80; }
  .dark .csj-wrap .lyrics  { color: #f1f5f9; }
  .dark .csj-wrap .comment { color: #4ade80; background: rgba(74,222,128,0.1); }
  /* Lyrics-only mode: hide all chords and chord-only rows */
  .csj-wrap.csj-lyrics-mode .chord { display: none !important; min-height: 0 !important; }
  .csj-wrap.csj-lyrics-mode .row.row--chord-only { display: none !important; }
  .csj-wrap.csj-lyrics-mode .column { flex-direction: row; }
  /* Merge syllables split by chord positions by removing inter-column spacing */
  .csj-wrap.csj-lyrics-mode .column .lyrics { padding-right: 0; }
  .csj-wrap.csj-lyrics-mode .column + .column .lyrics::before { content: ''; }
`;

const META_STRIP_RE = /^\{(title|subtitle|artist|composer|lyricist|album|key|capo|tempo|time|duration|year|ccli|copyright|flow|sorttitle|sortartist|url|google_doc_key)[^}]*\}\s*\n?/gim;
const _parser = new ChordProParser({ normalizeChords: false });
const _formatter = new HtmlDivFormatter({ normalizeChords: false });

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const PAGE_SIZE = 30;

const Songs = () => {
  const { user } = useAuth();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [languageFilter, setLanguageFilter] = useState('All');
  const [sortBy, setSortBy] = useState('updated');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null); // { processed, total, phase, is_incremental }
  const [lastSyncInfo, setLastSyncInfo] = useState(null);  // { last_sync_at, last_result }
  const [selectedSong, setSelectedSong] = useState(null);
  const [contentView, setContentView] = useState('chords'); // 'chords' | 'lyrics' | 'edit'
  const [semitoneOffset, setSemitoneOffset] = useState(0);
  const [accidental, setAccidental] = useState('#'); // '#' | 'b'
  const [capo, setCapo] = useState(0); // personal, device-only preference
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerSong, setViewerSong] = useState(null);
  const [viewerContent, setViewerContent] = useState('');
  const [viewerSemitoneOffset, setViewerSemitoneOffset] = useState(0);
  const pendingViewerOffsetRef = useRef(0); // ref used so the offset is captured synchronously before the render
  const pendingViewerAccidentalRef = useRef('#'); // same for accidental
  const lastNonEditViewRef = useRef('chords'); // remembers chords/lyrics view before entering edit mode
  const [drawerContent, setDrawerContent] = useState('');
  const [savingContent, setSavingContent] = useState(false);
  const [recentServices, setRecentServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [recentServiceIdx, setRecentServiceIdx] = useState(0);
  const [recentServicesOpen, setRecentServicesOpen] = useState(false);

  const skipRef = useRef(0);  // kept for legacy compat; actual pagination uses cursorRef
  const cursorRef = useRef(null);  // C1: next_cursor for start_after pagination
  const sentinelRef = useRef(null);
  const searchTimerRef = useRef(null);
  const isFirstLoad = useRef(true);
  const loadingMoreRef = useRef(false);  // sync guard for infinite scroll race condition
  const textareaRef = useRef(null);
  const isAdmin = canEdit(user);

  // ── Add Song dialog state ────────────────────────────────────────────────
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addContent, setAddContent] = useState('');
  const [addFields, setAddFields] = useState({ language: 'English' });
  const [addSaving, setAddSaving] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // ── Back-button closes modals / song drawer ──────────────────────────────
  const popstateSuppressedRef = useRef(false);
  const modalHistoryCountRef = useRef(0);
  const closeAnyOpenModalRef = useRef(null);
  const selectedSongRef = useRef(null);
  useEffect(() => { selectedSongRef.current = selectedSong; }, [selectedSong]);
  useEffect(() => {
    closeAnyOpenModalRef.current = () => {
      if (showDiscardConfirm) { setShowDiscardConfirm(false); return true; }
      if (showAddDialog) {
        setShowAddDialog(false);
        setAddContent('');
        setAddFields({ language: 'English' });
        return true;
      }
      if (selectedSongRef.current) {
        setSelectedSong(null);
        setDrawerContent('');
        setSemitoneOffset(0);
        setAccidental('#');
        setRecentServices([]);
        return true;
      }
      return false;
    };
  }, [showDiscardConfirm, showAddDialog]);
  useEffect(() => {
    const handlePopState = () => {
      if (popstateSuppressedRef.current) { popstateSuppressedRef.current = false; return; }
      if (closeAnyOpenModalRef.current?.()) {
        modalHistoryCountRef.current = Math.max(0, modalHistoryCountRef.current - 1);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Push a single history entry for any modal/panel open
  const openModalWithHistory = () => {
    window.history.pushState({ modal: true }, '');
    modalHistoryCountRef.current += 1;
  };
  // Close a dialog and pop its history entry cleanly (suppresses the resulting popstate)
  const closeModalCleanly = (setter) => {
    setter(false);
    if (modalHistoryCountRef.current > 0) {
      popstateSuppressedRef.current = true;
      modalHistoryCountRef.current -= 1;
      window.history.back();
    }
  };
  // Close the Add Song dialog (and any nested Discard confirm) in one go
  const closeAddDialogFull = () => {
    const count = modalHistoryCountRef.current;
    setShowAddDialog(false);
    setAddContent('');
    setAddFields({ language: 'English' });
    setShowDiscardConfirm(false);
    if (count > 0) {
      popstateSuppressedRef.current = true;
      modalHistoryCountRef.current = 0;
      window.history.go(-count);
    }
  };

  const tryCloseAddDialog = () => {
    if (addSaving) return;
    if (addContent.trim()) {
      openModalWithHistory();
      setShowDiscardConfirm(true);
    } else {
      closeAddDialogFull();
    }
  };


  const handleAddSong = async () => {
    if (!addContent.trim()) { toast.error('ChordPro content is required'); return; }
    setAddSaving(true);
    try {
      const res = await songsAPI.create({
        title: '',
        artist: '',
        language: addFields.language,
        content: addContent,
      });
      const { dropbox_updated, dropbox_error } = res.data;
      if (dropbox_updated) toast.success('Song added and synced to Dropbox');
      else if (dropbox_error) toast.warning(`Song added — Dropbox sync failed: ${dropbox_error}`);
      else toast.success('Song added to library');
      closeAddDialogFull();
      loadSongs();
    } catch {
      toast.error('Failed to add song');
    } finally {
      setAddSaving(false);
    }
  };

  // Core paginated fetch — uses cursor-based start_after pagination (C1)
  const fetchPage = useCallback(async (cursor, search, language, reset, sort_by = 'title') => {
    if (reset) { setLoading(true); }
    else { setLoadingMore(true); }
    try {
      const res = await songsAPI.getAll({
        cursor: cursor || undefined,
        limit: PAGE_SIZE,
        search,
        language: language === 'All' ? '' : language,
        sort_by,
      });
      const { items, total: t, has_more, next_cursor } = res.data;
      setSongs(prev => reset ? items : [...prev, ...items]);
      if (t != null) setTotal(t);  // only update on first page (backend omits on page 2+)
      setHasMore(has_more);
      cursorRef.current = next_cursor || null;
      skipRef.current = reset ? items.length : skipRef.current + items.length;
    } catch {
      if (reset) toast.error('Failed to load songs');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, []);

  // Initial load + debounced filter/search reload
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      cursorRef.current = null;
      fetchPage(null, searchQuery, languageFilter, true, sortBy);
      return;
    }
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      cursorRef.current = null;
      fetchPage(null, searchQuery, languageFilter, true, sortBy);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery, languageFilter, sortBy, fetchPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload from scratch (called after delete/sync)
  const loadSongs = useCallback(() => {
    cursorRef.current = null;
    fetchPage(null, searchQuery, languageFilter, true, sortBy);
  }, [searchQuery, languageFilter, sortBy, fetchPage]);

  // Load last sync info on mount
  useEffect(() => {
    if (!isAdmin) return;
    songsAPI.getSyncGlobalStatus().then(res => {
      setLastSyncInfo({ last_sync_at: res.data.last_sync_at, last_result: res.data.last_result });
    }).catch(() => {});
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) {
          loadingMoreRef.current = true;  // set sync guard BEFORE async fetchPage
          fetchPage(cursorRef.current, searchQuery, languageFilter, false, sortBy);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.unobserve(sentinel);
  }, [hasMore, loadingMore, loading, fetchPage, searchQuery, languageFilter, sortBy]);

  // Auto-resize textarea to fit content — deferred so the DOM is ready after conditional render
  useEffect(() => {
    const resize = () => {
      const el = textareaRef.current;
      if (!el) return;
      const scrollY = window.scrollY; // save scroll position before height collapse
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
      window.scrollTo(0, scrollY); // restore — prevents page jumping on every keystroke
    };
    const t = setTimeout(resize, 0);
    return () => clearTimeout(t);
  }, [drawerContent, contentView]);

  const openDrawer = useCallback(async (song) => {
    openModalWithHistory();
    setSelectedSong(song);
    setDrawerContent('');
    setContentView('chords');
    setRecentServiceIdx(0);
    setRecentServicesOpen(false);
    setSemitoneOffset(0);
    setAccidental(detectAccidental(song.key)); // auto-detect from song's key
    setCapo(getStoredCapo(song.song_id) ?? 0);
    setRecentServices([]);
    setRecentServicesOpen(false);
    setLoadingServices(true);

    // Fetch full song (with content) + recent services in parallel
    const [fullRes, svcRes] = await Promise.allSettled([
      songsAPI.getById(song.song_id),
      songsAPI.getRecentServices(song.song_id),
    ]);
    if (fullRes.status === 'fulfilled') {
      const full = fullRes.value.data;
      setDrawerContent(full.content || '');
      // Merge any fields from full song (bpm, key, etc.) back into selectedSong
      setSelectedSong(prev => prev ? { ...prev, ...full } : full);
      if (getStoredCapo(song.song_id) === null) {
        const fc = parseCapoDirective(full.content || '');
        if (fc !== null) setCapo(fc);
      }
    }
    if (svcRes.status === 'fulfilled') {
      const svcs = svcRes.value.data;
      setRecentServices(svcs);
      if (svcs.length > 0) setRecentServicesOpen(true);
    }
    setLoadingServices(false);
  }, []);

  const closeDrawer = useCallback(() => {
    setSelectedSong(null);
    setDrawerContent('');
    setSemitoneOffset(0);
    setAccidental('#');
    setCapo(0);
    setRecentServices([]);
    if (modalHistoryCountRef.current > 0) {
      popstateSuppressedRef.current = true;
      modalHistoryCountRef.current -= 1;
      window.history.back();
    }
  }, []);

  const handleSaveContent = async () => {
    if (!selectedSong) return;
    setSavingContent(true);
    try {
      const res = await songsAPI.updateContent(selectedSong.song_id, drawerContent);
      const { meta = {}, dropbox_updated, dropbox_error } = res.data;
      const updatedAt = new Date().toISOString();
      const patch = { content: drawerContent, updated_at: updatedAt, ...meta };
      setSongs(prev => prev.map(s => s.song_id === selectedSong.song_id ? { ...s, ...patch } : s));
      setSelectedSong(prev => prev ? { ...prev, ...patch } : null);
      // Keep the offline cache in sync so SetlistTab shows the new content immediately
      try { localStorage.setItem(`song_content_${selectedSong.song_id}`, drawerContent); } catch { /* quota */ }
      if (dropbox_updated) {
        toast.success('Content saved and synced to Dropbox');
      } else if (dropbox_error) {
        toast.warning(`Content saved — Dropbox sync failed: ${dropbox_error}`);
      } else {
        toast.success('Content saved');
      }
    } catch {
      toast.error('Failed to save content');
    } finally {
      setSavingContent(false);
    }
  };

  const handlePrint = useCallback(() => {
    if (!selectedSong) return;
    const isLyrics = contentView === 'lyrics';
    const displayKey = selectedSong.key ? transposeKey(selectedSong.key, semitoneOffset, accidental) : '';

    // Build content HTML — compute chord chart inline to avoid dependency ordering issues
    let innerHtml;
    if (isLyrics) {
      try {
        const cleaned = drawerContent.replace(META_STRIP_RE, '');
        let parsed = _parser.parse(cleaned);
        if (semitoneOffset !== 0) parsed = parsed.transpose(semitoneOffset);
        if (capo) parsed = parsed.transpose(-capo);
        const html = postProcessChordHtml(_formatter.format(parsed), accidental);
        innerHtml = `<div class="csj-wrap csj-lyrics-mode">${html}</div>`;
      } catch {
        innerHtml = '<p style="color:#94a3b8;font-size:13px">No lyrics available</p>';
      }
    } else {
      try {
        const cleaned = drawerContent.replace(META_STRIP_RE, '');
        let parsed = _parser.parse(cleaned);
        if (semitoneOffset !== 0) parsed = parsed.transpose(semitoneOffset);
        if (capo) parsed = parsed.transpose(-capo);
        innerHtml = `<div class="csj-wrap">${postProcessChordHtml(_formatter.format(parsed), accidental)}</div>`;
      } catch {
        innerHtml = '<p style="color:#94a3b8;font-size:13px">No chord chart available</p>';
      }
    }

    const metaParts = [];
    if (displayKey) metaParts.push(`Key: <strong style="font-family:monospace">${displayKey}</strong>`);
    if (selectedSong.bpm) metaParts.push(`BPM: <strong style="font-family:monospace">${selectedSong.bpm}</strong>`);
    if (capo) metaParts.push(`Capo: <strong style="font-family:monospace">${capo}</strong>`);

    const fullHtml = `
      <div style="font-family:sans-serif;max-width:680px;margin:0 auto;color:#0f172a">
        <div style="margin-bottom:16px;padding-bottom:12px;border-bottom:2px solid #e2e8f0">
          <h1 style="font-size:18px;font-weight:700;margin:0;line-height:1.3">${selectedSong.title}${selectedSong.artist ? `<span style="font-size:13px;color:#64748b;font-weight:400"> — ${selectedSong.artist}</span>` : ''}</h1>
          ${metaParts.length ? `<div style="display:flex;gap:16px;margin-top:6px;font-size:12px;color:#475569">${metaParts.join(' <span style="color:#cbd5e1">·</span> ')}</div>` : ''}
        </div>
        ${innerHtml}
        <p style="margin-top:32px;padding-top:10px;border-top:1px solid #e2e8f0;font-size:10px;color:#94a3b8;text-align:right">
          Church Service Management · ${new Date().toLocaleString()}
        </p>
      </div>`;

    const printCss = `
      #song-print-portal { display: none; }
      @media print {
        @page { margin: 1.5cm; size: A4 portrait; }
        html, body { height: auto !important; overflow: visible !important; background: white !important; }
        body > *:not(#song-print-portal) { display: none !important; }
        #song-print-portal { display: block !important; position: static !important; width: 100% !important; }
        .csj-wrap .paragraph { margin-bottom: 1rem; }
        .csj-wrap .row { display: flex; flex-wrap: wrap; line-height: 1; }
        .csj-wrap .column { display: inline-flex; flex-direction: column; }
        .csj-wrap .chord { font-family: monospace; font-size: 10.5px; font-weight: 700; color: #16a34a !important; line-height: 1.3; min-height: 1.3em; white-space: pre; padding-right: 0.4em; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .csj-wrap .chord:empty { visibility: hidden; }
        .csj-wrap .lyrics { font-family: monospace; font-size: 12px; line-height: 1.5; color: #1e293b !important; white-space: pre-wrap; word-break: break-word; padding-right: 0.4em; }
        .csj-wrap .comment { display: inline-block; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #16a34a !important; background: rgba(22,163,74,0.1) !important; border-radius: 3px; padding: 1px 5px; margin: 3px 0 2px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .csj-wrap.csj-lyrics-mode .chord { display: none !important; }
        .csj-wrap.csj-lyrics-mode .row.row--chord-only { display: none !important; }
        .csj-wrap.csj-lyrics-mode .column { flex-direction: row; }
      }`;

    document.getElementById('song-print-css')?.remove();
    const style = document.createElement('style');
    style.id = 'song-print-css';
    style.textContent = printCss;
    document.head.appendChild(style);

    let portal = document.getElementById('song-print-portal');
    if (!portal) { portal = document.createElement('div'); portal.id = 'song-print-portal'; document.body.appendChild(portal); }
    portal.innerHTML = fullHtml;

    window.print();
    const cleanup = () => {
      document.getElementById('song-print-css')?.remove();
      document.getElementById('song-print-portal')?.remove();
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
  }, [selectedSong, semitoneOffset, accidental, contentView, drawerContent, capo]);

  const handleDelete = async (songId, e) => {
    e?.stopPropagation();
    try {
      await songsAPI.delete(songId);
      toast.success('Song deleted');
      if (selectedSong?.song_id === songId) closeDrawer();
      loadSongs();
    } catch {
      toast.error('Failed to delete song');
    }
  };

  const handleSyncDropbox = async () => {
    closeDrawer(); // navigate back to list before syncing
    setSyncing(true);
    setSyncResult(null);
    setSyncProgress({ processed: 0, total: 0, phase: 'starting', is_incremental: null });
    try {
      let startRes;
      try {
        startRes = await songsAPI.syncDropbox();
      } catch (startErr) {
        // 409 = a previous sync got stuck. Auto-reset and retry once.
        if (startErr?.response?.status === 409) {
          toast.info('Previous sync was stuck — resetting and retrying...');
          await songsAPI.resetStuckSync();
          startRes = await songsAPI.syncDropbox();
        } else {
          throw startErr;
        }
      }
      const { job_id } = startRes.data;

      const result = await new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const statusRes = await songsAPI.getSyncStatus(job_id);
            const job = statusRes.data;
            setSyncProgress({
              processed: job.processed || 0,
              total: job.total || 0,
              phase: job.phase || 'running',
              is_incremental: job.is_incremental,
            });
            if (job.status === 'done') { clearInterval(interval); resolve(job.result); }
            else if (job.status === 'error') { clearInterval(interval); reject(new Error(job.error || 'Sync failed')); }
          } catch (e) { clearInterval(interval); reject(e); }
        }, 1500);
      });

      setSyncResult(result);
      // Update last sync info display
      setLastSyncInfo({
        last_sync_at: new Date().toISOString(),
        last_result: {
          is_incremental: result.is_incremental,
          added: result.added.length,
          updated_from_dropbox: result.updated_from_dropbox.length,
          uploaded_to_dropbox: result.uploaded_to_dropbox.length,
          deleted_from_app: result.deleted_from_app?.length || 0,
          unchanged: result.unchanged,
          errors: result.errors.length,
        },
      });
      const totalChanges = result.added.length + result.updated_from_dropbox.length + result.uploaded_to_dropbox.length + (result.deleted_from_app?.length || 0);
      if (totalChanges === 0 && !result.errors.length) {
        toast.success(`Already up to date — ${result.unchanged} song${result.unchanged !== 1 ? 's' : ''} unchanged`);
      } else {
        toast.success(`Sync complete — ${totalChanges} change${totalChanges !== 1 ? 's' : ''}`);
      }
      if (result.errors.length) toast.error(`${result.errors.length} file${result.errors.length !== 1 ? 's' : ''} had errors`);
      loadSongs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Sync failed');
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  // ChordSheetJS chord chart — strip metadata directives, then parse+format
  const chartHtml = useMemo(() => {
    if (!drawerContent) return '';
    try {
      const cleaned = drawerContent.replace(META_STRIP_RE, '');
      let parsed = _parser.parse(cleaned);
      if (semitoneOffset !== 0) parsed = parsed.transpose(semitoneOffset);
      if (capo) parsed = parsed.transpose(-capo);
      return postProcessChordHtml(_formatter.format(parsed), accidental);
    } catch { return ''; }
  }, [drawerContent, semitoneOffset, accidental, capo]);

  // Step capo — personal preference, saved to this device only
  const stepCapo = useCallback((delta) => {
    setCapo(c => {
      const next = Math.max(CAPO_MIN, Math.min(CAPO_MAX, c + delta));
      if (selectedSong) setStoredCapo(selectedSong.song_id, next);
      return next;
    });
  }, [selectedSong]);

  return (
    <div className="bg-slate-50 pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5 sm:mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-green-700 mb-1">Library</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Song Library</h1>
            <p className="mt-1 text-sm text-slate-500">
              {loading ? 'Loading…' : `${total.toLocaleString()} song${total !== 1 ? 's' : ''}`}
            </p>
          </div>
          {isAdmin && (
            <div className="flex flex-col items-start sm:items-end gap-1.5 self-start sm:self-auto">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { openModalWithHistory(); setShowAddDialog(true); }}
                  data-testid="add-song-btn"
                  className="h-9 sm:h-10 px-4 sm:px-5 rounded-full bg-slate-800 text-white hover:bg-slate-700 transition-all duration-200 shadow-sm font-medium flex items-center gap-2 text-sm whitespace-nowrap"
                  title="Add Song"
                >
                  <Plus className="w-4 h-4 flex-shrink-0" />
                  Add Song
                </button>
                <button
                  onClick={handleSyncDropbox}
                  disabled={syncing}
                  data-testid="sync-dropbox-btn"
                  className="h-9 sm:h-10 px-4 sm:px-6 rounded-full bg-green-700 text-white hover:bg-green-800 transition-all duration-200 shadow-sm font-medium flex items-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  title="Sync Dropbox"
                >
                  {syncing
                    ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    : <RefreshCw className="w-4 h-4 flex-shrink-0" />
                  }
                  {syncing ? 'Syncing…' : 'Sync Dropbox'}
                </button>
              </div>
              {/* Last sync info */}
              {!syncing && lastSyncInfo?.last_sync_at && (
                <p className="text-xs text-slate-400" data-testid="last-sync-info">
                  Last: {formatRelativeTime(lastSyncInfo.last_sync_at)}
                  {lastSyncInfo.last_result?.is_incremental != null && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-medium text-slate-500">
                      {lastSyncInfo.last_result.is_incremental ? 'incremental' : 'full'}
                    </span>
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Sync Progress Bar */}
        {syncing && syncProgress && (
          <div className="mb-4 p-4 bg-white border border-slate-200 rounded-xl" data-testid="sync-progress">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <span className="w-3.5 h-3.5 border-2 border-green-600 border-t-transparent rounded-full animate-spin inline-block" />
                <span className="capitalize">{syncProgress.phase === 'listing' ? 'Listing Dropbox files…' : syncProgress.phase === 'comparing' ? 'Comparing with library…' : syncProgress.phase === 'downloading' ? 'Downloading changes…' : syncProgress.phase === 'uploading' ? 'Uploading to Dropbox…' : syncProgress.phase === 'finalizing' ? 'Finalizing…' : 'Starting…'}</span>
              </div>
              <div className="flex items-center gap-2">
                {syncProgress.is_incremental != null && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${syncProgress.is_incremental ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                    {syncProgress.is_incremental ? 'incremental' : 'full sync'}
                  </span>
                )}
                {syncProgress.total > 0 && (
                  <span className="text-xs text-slate-500">{syncProgress.processed}/{syncProgress.total}</span>
                )}
              </div>
            </div>
            {syncProgress.total > 0 && (
              <div className="w-full bg-slate-100 rounded-full h-1.5">
                <div
                  className="bg-green-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, (syncProgress.processed / syncProgress.total) * 100)}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Sync Result Summary */}
        {syncResult && !syncing && (
          <div className="mb-4 p-4 bg-white border border-slate-200 rounded-xl flex flex-wrap gap-3 items-start" data-testid="sync-result">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span>Sync complete</span>
              {syncResult.is_incremental != null && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${syncResult.is_incremental ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                  {syncResult.is_incremental ? 'incremental' : 'full sync'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              {syncResult.added.length > 0 && <span className="text-green-700 font-medium">+{syncResult.added.length} added</span>}
              {syncResult.updated_from_dropbox.length > 0 && <span className="text-blue-600 font-medium">↓ {syncResult.updated_from_dropbox.length} updated from Dropbox</span>}
              {syncResult.uploaded_to_dropbox.length > 0 && <span className="text-purple-600 font-medium">↑ {syncResult.uploaded_to_dropbox.length} uploaded to Dropbox</span>}
              {syncResult.deleted_from_app?.length > 0 && <span className="text-rose-600 font-medium">✕ {syncResult.deleted_from_app.length} removed from library</span>}
              {syncResult.unchanged > 0 && <span className="text-slate-400">{syncResult.unchanged} unchanged</span>}
              {syncResult.errors.length > 0 && (
                <span className="text-rose-600 font-medium flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> {syncResult.errors.length} error{syncResult.errors.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <button onClick={() => setSyncResult(null)} className="ml-auto text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Search + Language Filter */}
        {!selectedSong && (
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="search-songs-input" placeholder="Search songs by title or artist..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-white h-10" />
          </div>
          <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="language-filter">
            {['All', 'English', 'Filipino'].map(lang => (
              <button key={lang} onClick={() => setLanguageFilter(lang)} data-testid={`filter-lang-${lang.toLowerCase()}`}
                className={`px-3 sm:px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  languageFilter === lang ? 'bg-green-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                }`}>
                {lang}
              </button>
            ))}
          </div>
          {/* Sort toggle */}
          <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 flex-shrink-0" data-testid="sort-filter">
            <button onClick={() => setSortBy('title')} data-testid="sort-az-btn"
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${sortBy === 'title' ? 'bg-green-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}>
              A-Z
            </button>
            <button onClick={() => setSortBy('updated')} data-testid="sort-recent-btn"
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${sortBy === 'updated' ? 'bg-green-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}>
              Recent
            </button>
          </div>
        </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700 mx-auto"></div>
          </div>
        ) : songs.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-xl">
            <MusicIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              {searchQuery || languageFilter !== 'All' ? 'No songs match your search' : 'No songs in library yet'}
            </p>
            {!searchQuery && languageFilter === 'All' && isAdmin && (
              <p className="mt-3 text-xs text-slate-400">Click <strong>Sync Repository</strong> to import songs from Dropbox</p>
            )}
          </div>
        ) : (
          <>
            {/* Song list — hidden when a song is selected */}
            {!selectedSong && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="songs-list">
              <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <div className="col-span-6">Title / Artist</div>
                <div className="col-span-2">Language</div>
                <div className="col-span-1">Key</div>
                <div className="col-span-1">BPM</div>
                <div className="col-span-2">Updated</div>
              </div>
              {songs.map((song) => (
                <div key={song.song_id} data-testid={`song-item-${song.song_id}`}
                  onClick={() => openDrawer(song)}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-5 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-green-600/10 transition-colors items-center cursor-pointer">
                  <div className="col-span-6 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{song.title}</p>
                    <p className="text-xs text-slate-500 truncate">{song.artist}</p>
                  </div>
                  <div className="col-span-2">
                    {song.language && (
                      <span className="inline-block px-2.5 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                        {song.language}
                      </span>
                    )}
                  </div>
                  <div className="col-span-1 text-sm text-slate-600 font-mono">{song.key || '-'}</div>
                  <div className="col-span-1 text-sm text-slate-600 font-mono" data-testid={`song-bpm-${song.song_id}`}>{song.bpm || '-'}</div>
                  <div className="col-span-2 text-xs text-slate-500" data-testid={`song-updated-${song.song_id}`}>
                    {formatRelativeTime(song.updated_at || song.created_at)}
                  </div>
                </div>
              ))}
              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} />
              {loadingMore && (
                <div className="flex items-center justify-center py-6 border-t border-slate-100" data-testid="loading-more">
                  <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin mr-2" />
                  <span className="text-sm text-slate-500">Loading more songs…</span>
                </div>
              )}
            </div>
            )}

            {/* Song detail — full width when selected */}
            {selectedSong && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="song-detail-drawer">
                <style>{CHORD_SHEET_CSS}</style>
                <div className="flex flex-col">
                  {/* Header */}
                  <div className="px-6 pt-5 pb-4 border-b border-slate-100 shrink-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-slate-900 leading-tight" data-testid="drawer-song-title">{selectedSong.title}</h2>
                        <p className="text-sm text-slate-500 mt-0.5">{selectedSong.artist}</p>
                        {selectedSong.language && (
                          <span className="inline-block mt-1 px-2.5 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                            {selectedSong.language}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={closeDrawer}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                          aria-label="Back to list"
                        >
                          <ChevronLeft className="w-4 h-4" /> Back
                        </button>
                      </div>
                    </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-xs text-slate-500 items-center">
                    {selectedSong.key && (
                      <div className="flex items-center gap-0.5" data-testid="drawer-key-control">
                        <span className="mr-0.5">Key:</span>
                        <button
                          onClick={() => setSemitoneOffset(n => n - 1)}
                          data-testid="drawer-key-down"
                          className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100 font-bold transition-colors leading-none"
                          aria-label="Transpose down"
                        >−</button>
                        <button
                          onClick={() => semitoneOffset !== 0 && setSemitoneOffset(0)}
                          data-testid="drawer-key-display"
                          title={semitoneOffset !== 0 ? `Original: ${selectedSong.key} — click to reset` : selectedSong.key}
                          className={`font-mono font-semibold min-w-[2rem] text-center px-1 py-0.5 rounded transition-colors ${
                            semitoneOffset !== 0
                              ? 'text-green-700 bg-green-50 cursor-pointer hover:bg-green-100'
                              : 'text-slate-700 cursor-default'
                          }`}
                        >
                          {transposeKey(selectedSong.key, semitoneOffset, accidental)}
                        </button>
                        <button
                          onClick={() => setSemitoneOffset(n => n + 1)}
                          data-testid="drawer-key-up"
                          className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100 font-bold transition-colors leading-none"
                          aria-label="Transpose up"
                        >+</button>
                        {/* Sharp / Flat toggle */}
                        <button
                          onClick={() => setAccidental(a => a === 'b' ? '#' : 'b')}
                          data-testid="drawer-accidental-toggle"
                          title={accidental === 'b' ? 'Currently: flats — click for sharps' : 'Currently: sharps — click for flats'}
                          className="w-5 h-5 flex items-center justify-center rounded text-xs font-bold ml-1 bg-slate-200 text-slate-600 hover:bg-slate-300 transition-colors"
                        >
                          {accidental === 'b' ? '♭' : '♯'}
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-0.5" data-testid="drawer-capo-control">
                      <span className="mr-0.5">Capo:</span>
                      <button
                        onClick={() => stepCapo(-1)}
                        disabled={capo <= CAPO_MIN}
                        data-testid="drawer-capo-down"
                        className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100 font-bold transition-colors leading-none disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Decrease capo"
                      >−</button>
                      <span
                        data-testid="drawer-capo-value"
                        title={capo > 0 ? `Capo on fret ${capo} — personal preference, saved to this device only` : 'No capo'}
                        className={`font-mono font-semibold min-w-[1.25rem] text-center px-1 py-0.5 rounded ${capo > 0 ? 'text-amber-700 bg-amber-50' : 'text-slate-700'}`}
                      >
                        {capo}
                      </span>
                      <button
                        onClick={() => stepCapo(1)}
                        disabled={capo >= CAPO_MAX}
                        data-testid="drawer-capo-up"
                        className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100 font-bold transition-colors leading-none disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Increase capo"
                      >+</button>
                    </div>
                    <span data-testid="drawer-song-bpm">BPM: <span className="font-mono text-slate-700">{selectedSong.bpm || '-'}</span></span>
                    <span>Updated: {formatRelativeTime(selectedSong.updated_at || selectedSong.created_at)}</span>
                  </div>
                  {selectedSong.notes && (
                    <p className="mt-2 text-xs text-slate-500 leading-relaxed">{selectedSong.notes}</p>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={handlePrint} data-testid="drawer-print-btn"
                      className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors flex items-center gap-1.5">
                      <Printer className="w-3 h-3" /> <span className="hidden sm:inline">Print</span>
                    </button>
                    <button
                      data-testid="drawer-fullscreen-btn"
                      onClick={() => {
                        pendingViewerOffsetRef.current = semitoneOffset;
                        pendingViewerAccidentalRef.current = accidental;
                        setViewerSong(selectedSong);
                        setViewerContent(drawerContent);
                        setViewerSemitoneOffset(semitoneOffset);
                        window.history.pushState({ viewer: 'song' }, '');
                        setViewerOpen(true);
                        // Clear drawer state but keep the history entry — the viewer session inherits it
                        setSelectedSong(null);
                        setDrawerContent('');
                        setSemitoneOffset(0);
                        setAccidental('#');
                        setRecentServices([]);
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors flex items-center gap-1.5"
                      title="Full screen"
                    >
                      <Maximize2 className="w-3 h-3" /> <span className="hidden sm:inline">Full screen</span>
                    </button>
                    {isAdmin && (
                      <DeleteButton
                        onConfirm={(e) => handleDelete(selectedSong.song_id, e)}
                        className="ml-auto p-1.5 rounded-lg flex items-center gap-1.5"
                        title="Delete song"
                        testId="drawer-delete-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline text-xs font-medium">Delete</span>
                      </DeleteButton>
                    )}
                  </div>
                </div>

                {/* Recent Services — single item by default, expandable */}
                <div className="px-6 py-3 border-b border-slate-100" data-testid="recent-services-list">
                  {loadingServices ? (
                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600"></div> Loading…
                    </div>
                  ) : recentServices.length === 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Recent Services</span>
                      <span className="text-xs text-slate-400 italic" data-testid="no-recent-services">Not used in any service yet</span>
                    </div>
                  ) : (
                    <>
                      {/* Header row — label + count + collapse toggle */}
                      <button
                        onClick={() => setRecentServicesOpen(o => !o)}
                        className="flex items-center gap-2 w-full text-left group mb-2"
                      >
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Recent Services</span>
                        <span className="text-xs text-slate-400">({recentServices.length})</span>
                        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 ml-auto transition-transform duration-200 ${recentServicesOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {recentServicesOpen ? (
                        /* Expanded: all services */
                        <div className="space-y-2">
                          {recentServices.map((svc) => (
                            <div key={svc.service_id} data-testid={`recent-service-${svc.service_id}`}
                              className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                              <div className="h-6 w-6 rounded bg-green-100 text-green-700 flex items-center justify-center shrink-0">
                                <Calendar className="w-3 h-3" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-slate-800 truncate">{svc.name || svc.service_type}</p>
                                <p className="text-xs text-slate-500">{new Date(svc.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{svc.time && ` · ${svc.time}`}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        /* Collapsed: single item with next arrow */
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex items-center gap-2 min-w-0 flex-1" data-testid={`recent-service-${recentServices[recentServiceIdx]?.service_id}`}>
                            <div className="h-6 w-6 rounded bg-green-100 text-green-700 flex items-center justify-center shrink-0">
                              <Calendar className="w-3 h-3" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-slate-800 truncate">{recentServices[recentServiceIdx]?.name || recentServices[recentServiceIdx]?.service_type}</p>
                              <p className="text-xs text-slate-500">{new Date(recentServices[recentServiceIdx]?.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                            </div>
                          </div>
                          {recentServices.length > 1 && (
                            <div className="flex items-center gap-1 shrink-0">
                              <span className="text-xs text-slate-400 tabular-nums">{recentServiceIdx + 1}/{recentServices.length}</span>
                              <button
                                onClick={() => setRecentServiceIdx(i => (i + 1) % recentServices.length)}
                                className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                                aria-label="Next service"
                              >
                                <ChevronRight className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Content section — chord chart / lyrics / edit */}
                <div className="px-6 py-4 flex flex-col border-b border-slate-100">
                  {/* View-mode toggle */}
                  <div className="flex items-center gap-2 mb-3 shrink-0">
                    {/* Joined button group: Chords↔Lyrics toggle | Edit */}
                    <div className="flex items-center rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600 divide-x divide-slate-200 dark:divide-slate-600">
                      <button
                        data-testid="drawer-view-toggle"
                        onClick={() => setContentView(v => {
                          if (v === 'edit') return lastNonEditViewRef.current;
                          const next = v === 'lyrics' ? 'chords' : 'lyrics';
                          lastNonEditViewRef.current = next;
                          return next;
                        })}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all ${
                          contentView === 'chords' || contentView === 'lyrics'
                            ? 'bg-green-600 text-white'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        {(contentView === 'edit' ? lastNonEditViewRef.current : contentView) === 'lyrics'
                          ? <><AlignLeft className="w-3.5 h-3.5" /> Lyrics</>
                          : <><Music2 className="w-3.5 h-3.5" /> Chords</>
                        }
                      </button>
                      {isAdmin && (
                        <button
                          data-testid="drawer-view-edit"
                          onClick={() => { if (contentView !== 'edit') lastNonEditViewRef.current = contentView; setContentView('edit'); }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all ${
                            contentView === 'edit'
                              ? 'bg-amber-500 text-white'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                          }`}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                      )}
                    </div>
                    {isAdmin && contentView === 'edit' && drawerContent !== (selectedSong.content || '') && (
                      <span className="ml-auto text-xs text-amber-600 font-medium">Unsaved</span>
                    )}                  </div>

                  {/* Content display */}
                  {contentView === 'edit' ? (
                    <Textarea
                      ref={textareaRef}
                      data-testid="drawer-content-textarea"
                      value={drawerContent}
                      onChange={(e) => setDrawerContent(e.target.value)}
                      placeholder="ChordPro content will appear here after sync…"
                      className="w-full font-mono text-xs resize-none scrollbar-none border-0 focus-visible:ring-0 p-3 overflow-hidden"
                      style={{ scrollbarWidth: 'none', height: 'auto' }}
                    />
                  ) : contentView === 'lyrics' ? (
                    <div data-testid="drawer-content-lyrics">
                      {drawerContent ? (
                        <div className="csj-wrap csj-lyrics-mode" dangerouslySetInnerHTML={{ __html: chartHtml }} />
                      ) : (
                        <span className="text-slate-400 italic text-xs">No content yet</span>
                      )}
                    </div>
                  ) : (
                    /* Chords view (default) */
                    <div data-testid="drawer-content-chords">
                      {drawerContent ? (
                        <div className="csj-wrap" dangerouslySetInnerHTML={{ __html: chartHtml }} />
                      ) : (
                        <p className="text-slate-400 text-xs italic mt-2">No content yet — run Sync Repository</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Save / Cancel — below content */}
                {isAdmin && (
                  <div className="px-6 py-3 border-b border-slate-100 flex gap-2">
                    <button
                      data-testid="drawer-save-content-btn"
                      onClick={handleSaveContent}
                      disabled={savingContent || drawerContent === (selectedSong.content || '')}
                      className="flex-1 px-3 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <CheckCircle className="w-3.5 h-3.5" /> {savingContent ? 'Saving…' : 'Save Content'}
                    </button>
                    {drawerContent !== (selectedSong.content || '') && (
                      <button
                        data-testid="drawer-cancel-content-btn"
                        onClick={() => {
                          setDrawerContent(selectedSong.content || '');
                          setContentView(lastNonEditViewRef.current);
                        }}
                        className="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                )}

              </div>
            </div>
            )}
          </>
        )}
      </div>
      {/* Full-screen viewer */}
      {viewerOpen && viewerSong && (
        <SongViewerModal
          songs={[viewerSong]}
          contents={{ [viewerSong.song_id]: viewerContent }}
          loadingContent={{}}
          initialIndex={0}
          initialSemitoneOffset={pendingViewerOffsetRef.current}
          initialAccidental={pendingViewerAccidentalRef.current}
          onFetchContent={() => {}}
          onClose={(closedOffset = 0, closedAccidental = '#') => {
            setViewerOpen(false);
            // Re-open the song drawer, restoring the key offset and accidental from the fullscreen session
            const song = viewerSong;
            const content = viewerContent;
            setViewerSong(null);
            setSelectedSong(song);
            setDrawerContent(content);
            setSemitoneOffset(closedOffset);
            setAccidental(closedAccidental);
            setCapo(getStoredCapo(song.song_id) ?? 0);
            setLoadingServices(true);
            songsAPI.getRecentServices(song.song_id)
              .then(res => {
                setRecentServices(res.data);
                if (res.data.length > 0) setRecentServicesOpen(true);
              })
              .catch(() => {})
              .finally(() => setLoadingServices(false));
          }}
          hideNav
        />
      )}

      {/* ── Add Song Dialog ─────────────────────────────────────────────── */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { if (!open) tryCloseAddDialog(); }}>
        <DialogContent className="sm:max-w-2xl bg-white" data-testid="add-song-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">Add New Song</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Language — determines Dropbox folder */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Language</label>
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="add-song-language">
                {['English', 'Filipino'].map(lang => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setAddFields(f => ({ ...f, language: lang }))}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${addFields.language === lang ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>

            {/* ChordPro textarea */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">ChordPro Content</label>
              <Textarea
                data-testid="add-song-chordpro-input"
                value={addContent}
                onChange={e => setAddContent(e.target.value)}
                placeholder="{title: Amazing Grace}&#10;{artist: John Newton}&#10;{key: G}&#10;{tempo: 72}&#10;&#10;{c: Verse 1}&#10;[G]Amazing [G7]grace, how [C]sweet the [G]sound..."
                className="font-mono text-xs resize-none h-64 bg-slate-50 border-slate-200 focus:border-green-500 focus:ring-green-500/20"
                spellCheck={false}
              />
              <p className="mt-1 text-xs text-slate-400">Title, artist, key and BPM are read from the ChordPro directives when saving.</p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={tryCloseAddDialog} disabled={addSaving} className="h-9">
              Cancel
            </Button>
            <Button
              data-testid="add-song-save-btn"
              onClick={handleAddSong}
              disabled={addSaving || !addContent.trim()}
              className="h-9 bg-green-700 hover:bg-green-800 text-white"
            >
              {addSaving ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</span> : 'Add Song'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discard confirmation — shown when closing Add Song dialog with unsaved content */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved ChordPro content. If you close now, your work will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => closeModalCleanly(setShowDiscardConfirm)} data-testid="discard-cancel-btn">Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={closeAddDialogFull} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="discard-confirm-btn">Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Songs;
